import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery } from '@tanstack/react-query';
import {
    Box, Paper, Typography, Button, CircularProgress, Alert,
    Stepper, Step, StepLabel, Container, TextField, Chip, Stack,
    ToggleButton, ToggleButtonGroup, Tooltip, IconButton, Divider, Collapse, Fade
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import config from './config.json';

// --- TYPES ---
interface OnboardingItem {
    OnboardingId: string;
    SortKey: string;
    content: string;
    type?: string;
    category?: string; // 'workforce' | 'project' | 'section'
    status_flag?: 'ADDITION' | 'MODIFICATION' | 'NO_CHANGE' | 'DUPLICATE';
    match_confidence?: number;
    matched_name?: string;
    is_user_edited?: boolean;
    original_data?: string; // JSON string of the existing record for comparison
}

interface OnboardingWorkbenchResponse {
    phase: string;
    items: OnboardingItem[];
    message?: string;
}

// --- LOCAL API SERVICE ---
const getApiAuthHeaders = (auth: any) => {
    const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
    const id_token = auth.user?.id_token;
    if (!userApiKey || !id_token) {
        throw new Error('Authentication credentials not available. Please log in again.');
    }
    return { 'Content-Type': 'application/json', 'X-Api-Key': userApiKey, 'Authorization': id_token };
};

const localApiService = {
    createOnboarding: async (auth: any, files: { filename: string; classification: string }[]) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/onboardings`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                files,
                cognitoGroups: auth.user?.profile?.["cognito:groups"]
            })
        });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    uploadFile: async (presignedUrl: string, file: File) => {
        const res = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
        });
        if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
        return true;
    },
    getWorkbench: async (onboardingId: string, auth: any): Promise<OnboardingWorkbenchResponse> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/onboardings/${onboardingId}/workbench`, { headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    updateItem: async (onboardingId: string, itemId: string, data: Partial<OnboardingItem>, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const encodedItemId = encodeURIComponent(itemId);
        const res = await fetch(`${baseUrl}/onboardings/${onboardingId}/items/${encodedItemId}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    regenerateItem: async (onboardingId: string, itemId: string, feedback: string, auth: any): Promise<{ content: string; previous_content: string }> => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const encodedItemId = encodeURIComponent(itemId);
        const res = await fetch(`${baseUrl}/onboardings/${onboardingId}/items/${encodedItemId}/regenerate`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ feedback })
        });
        if (!res.ok) {
            const errText = await res.text();
            try {
                throw new Error(JSON.parse(errText).message || errText);
            } catch {
                throw new Error(errText || 'Regeneration failed');
            }
        }
        return res.json();
    },
    approveStage: async (onboardingId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/onboardings/${onboardingId}/stage/approve`, { method: 'POST', headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
    skipStage: async (onboardingId: string, auth: any) => {
        const headers = getApiAuthHeaders(auth);
        const baseUrl = (config as any).api.tender.url;
        const res = await fetch(`${baseUrl}/onboardings/${onboardingId}/stage/skip`, { method: 'POST', headers });
        if (!res.ok) throw new Error((await res.json()).message);
        return res.json();
    },
};

// --- SUB-COMPONENT: File Upload Area ---
interface UploadFile {
    file: File;
    classification: 'cv' | 'project' | 'company' | 'auto';
}

const FileUploadArea: React.FC<{
    files: UploadFile[];
    onFilesChange: (files: UploadFile[]) => void;
    disabled: boolean;
}> = ({ files, onFilesChange, disabled }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const newFiles: UploadFile[] = Array.from(event.target.files).map(f => ({
                file: f,
                classification: 'auto' as const,
            }));
            onFilesChange([...files, ...newFiles]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleRemoveFile = (index: number) => {
        onFilesChange(files.filter((_, i) => i !== index));
    };

    const handleClassificationChange = (index: number, newClass: string) => {
        const updated = [...files];
        updated[index] = { ...updated[index], classification: newClass as UploadFile['classification'] };
        onFilesChange(updated);
    };

    return (
        <Box>
            <input ref={fileInputRef} type="file" multiple hidden accept=".pdf,.doc,.docx,.xlsx,.pptx" onChange={handleFileSelect} />
            <Button
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                fullWidth
                sx={{ mb: 2, py: 2, borderStyle: 'dashed' }}
            >
                Add Documents (CVs, Project Docs, Company Brochures)
            </Button>

            {files.length > 0 && (
                <Paper variant="outlined" sx={{ p: 1.5, maxHeight: '400px', overflowY: 'auto' }}>
                    {files.map((item, index) => (
                        <Box key={`${item.file.name}-${index}`} sx={{
                            display: 'flex', alignItems: 'center', gap: 1, py: 1,
                            borderBottom: index < files.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider'
                        }}>
                            <DescriptionIcon fontSize="small" color="action" />
                            <Tooltip title={item.file.name}>
                                <Typography variant="body2" sx={{
                                    flexGrow: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px'
                                }}>
                                    {item.file.name}
                                </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                ({(item.file.size / 1024 / 1024).toFixed(1)} MB)
                            </Typography>
                            <ToggleButtonGroup
                                value={item.classification}
                                exclusive
                                onChange={(_, val) => val && handleClassificationChange(index, val)}
                                size="small"
                                disabled={disabled}
                            >
                                <ToggleButton value="auto" sx={{ fontSize: '0.65rem', px: 1 }}>Auto</ToggleButton>
                                <ToggleButton value="cv" sx={{ fontSize: '0.65rem', px: 1 }}>CV</ToggleButton>
                                <ToggleButton value="project" sx={{ fontSize: '0.65rem', px: 1 }}>Project</ToggleButton>
                                <ToggleButton value="company" sx={{ fontSize: '0.65rem', px: 1 }}>Company</ToggleButton>
                            </ToggleButtonGroup>
                            <IconButton size="small" onClick={() => handleRemoveFile(index)} disabled={disabled} aria-label={`Remove ${item.file.name}`}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Paper>
            )}
        </Box>
    );
};

// --- SUB-COMPONENT: Diff Item Card ---
const DiffItemCard: React.FC<{
    item: OnboardingItem;
    onboardingId: string;
    auth: any;
}> = ({ item, onboardingId, auth }) => {
    const [content, setContent] = useState(item.content);
    const [statusFlag, setStatusFlag] = useState(item.status_flag || 'ADDITION');
    const [isDirty, setIsDirty] = useState(false);
    const [showRegen, setShowRegen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [previousContent, setPreviousContent] = useState<string | null>(null);
    const [regenError, setRegenError] = useState<string | null>(null);

    const updateMutation = useMutation({
        mutationFn: () => localApiService.updateItem(onboardingId, item.SortKey, {
            content,
            status_flag: statusFlag
        }, auth),
        onSuccess: () => setIsDirty(false)
    });

    const regenMutation = useMutation({
        mutationFn: () => localApiService.regenerateItem(onboardingId, item.SortKey, feedback, auth),
        onSuccess: (data) => {
            setPreviousContent(data.previous_content ?? content);
            setContent(data.content);
            setIsDirty(false);
            setShowRegen(false);
            setFeedback('');
            setRegenError(null);
        },
        onError: (err: Error) => {
            setRegenError(err.message || 'Regeneration failed');
        }
    });

    const undoMutation = useMutation({
        mutationFn: () => localApiService.updateItem(onboardingId, item.SortKey, {
            content: previousContent || '',
            status_flag: statusFlag
        }, auth),
        onSuccess: () => {
            if (previousContent !== null) setContent(previousContent);
            setPreviousContent(null);
            setIsDirty(false);
        }
    });

    const isRegenerating = regenMutation.isPending;
    const canRegenerate = statusFlag !== 'NO_CHANGE';

    // Color coding based on status
    let bgColor = 'white';
    let borderColor = '#e0e0e0';
    let statusLabel = 'Unknown';
    let statusChipColor: "default" | "success" | "warning" | "info" | "error" = "default";

    if (statusFlag === 'ADDITION') {
        bgColor = '#f6fff6';
        borderColor = '#4caf50';
        statusLabel = 'New Addition';
        statusChipColor = 'success';
    } else if (statusFlag === 'MODIFICATION') {
        bgColor = '#fffbf2';
        borderColor = '#ed6c02';
        statusLabel = 'Update Existing';
        statusChipColor = 'warning';
    } else if (statusFlag === 'NO_CHANGE') {
        bgColor = '#f5f5f5';
        borderColor = '#9e9e9e';
        statusLabel = 'No Change Needed';
        statusChipColor = 'default';
    } else if (statusFlag === 'DUPLICATE') {
        bgColor = '#fff5f5';
        borderColor = '#d32f2f';
        statusLabel = 'Possible Duplicate';
        statusChipColor = 'error';
    }

    // Parse category for display
    const categoryLabel = item.category === 'workforce' ? 'Workforce Member' :
                          item.category === 'project' ? 'Past Project' :
                          item.category === 'section' ? 'Company Section' : 'Item';

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2, mb: 2, bgcolor: bgColor,
                border: `1px solid ${borderColor}`,
                borderLeft: `6px solid ${borderColor}`,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: isRegenerating ? '0 0 0 3px rgba(156, 39, 176, 0.12)' : undefined,
                transition: 'box-shadow 200ms ease',
            }}
        >
            {isRegenerating && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, height: 3,
                        background: 'linear-gradient(90deg, #9c27b0, #2196f3, #9c27b0)',
                        backgroundSize: '200% 100%',
                        animation: 'qabotRegenShimmer 1.6s linear infinite',
                        '@keyframes qabotRegenShimmer': {
                            '0%':   { backgroundPosition: '200% 0' },
                            '100%': { backgroundPosition: '-200% 0' },
                        },
                    }}
                />
            )}
            {/* Header: Category + Status Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={categoryLabel} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    {item.match_confidence !== undefined && item.matched_name && (
                        <Tooltip title={`Matched with existing record: "${item.matched_name}" (${item.match_confidence}% confidence)`}>
                            <Chip
                                label={`Match: ${item.matched_name} (${item.match_confidence}%)`}
                                size="small"
                                variant="outlined"
                                color="info"
                                sx={{ fontSize: '0.65rem' }}
                            />
                        </Tooltip>
                    )}
                </Box>
                <ToggleButtonGroup
                    value={statusFlag}
                    exclusive
                    onChange={(_, val) => { if (val) { setStatusFlag(val); setIsDirty(true); }}}
                    size="small"
                >
                    <ToggleButton value="ADDITION" color="success">
                        <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} /> Add
                    </ToggleButton>
                    <ToggleButton value="MODIFICATION" color="warning">
                        <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} /> Update
                    </ToggleButton>
                    <ToggleButton value="NO_CHANGE">
                        Skip
                    </ToggleButton>
                    <ToggleButton value="DUPLICATE" color="error">
                        Duplicate
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Show existing data if this is a modification */}
            {item.original_data && statusFlag === 'MODIFICATION' && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1, borderLeft: '4px solid #9e9e9e' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                        Current Record
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontSize: '0.8rem' }}>
                        {item.original_data}
                    </Typography>
                </Box>
            )}

            {/* Editable Content */}
            <TextField
                label={`Extracted ${categoryLabel} Data`}
                fullWidth multiline minRows={3}
                value={content}
                onChange={(e) => { setContent(e.target.value); setIsDirty(true); }}
                variant="outlined"
                sx={{
                    mb: 1, bgcolor: 'white',
                    opacity: isRegenerating ? 0.55 : 1,
                    transition: 'opacity 200ms ease',
                }}
                disabled={statusFlag === 'NO_CHANGE' || isRegenerating}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={statusLabel} size="small" color={statusChipColor} sx={{ fontSize: '0.7rem' }} />
                    {previousContent !== null && !isDirty && (
                        <Chip
                            icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                            label="Regenerated"
                            size="small"
                            sx={{ fontSize: '0.7rem', bgcolor: '#f3e5f5', color: '#6a1b9a' }}
                        />
                    )}
                    {canRegenerate && !showRegen && !isRegenerating && (
                        <Tooltip title="Ask the AI to rewrite this with your feedback">
                            <Button
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => { setShowRegen(true); setRegenError(null); }}
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: '#9c27b0',
                                    color: '#6a1b9a',
                                    '&:hover': { borderColor: '#6a1b9a', bgcolor: 'rgba(156, 39, 176, 0.04)' },
                                    textTransform: 'none',
                                }}
                            >
                                Regenerate with AI
                            </Button>
                        </Tooltip>
                    )}
                    {previousContent !== null && !isRegenerating && (
                        <Tooltip title="Restore the version from before the last regeneration">
                            <span>
                                <Button
                                    startIcon={<UndoIcon />}
                                    onClick={() => undoMutation.mutate()}
                                    disabled={undoMutation.isPending}
                                    size="small"
                                    variant="text"
                                    color="inherit"
                                    sx={{ textTransform: 'none' }}
                                >
                                    {undoMutation.isPending ? 'Restoring...' : 'Undo regeneration'}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>
                {isDirty && (
                    <Button
                        startIcon={<SaveIcon />}
                        disabled={updateMutation.isPending || isRegenerating}
                        onClick={() => updateMutation.mutate()}
                        size="small"
                        variant="contained"
                        color="warning"
                    >
                        {updateMutation.isPending ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </Box>

            <Collapse in={showRegen} timeout={250} unmountOnExit>
                <Box
                    sx={{
                        mt: 2, p: 2, borderRadius: 1,
                        background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.06), rgba(33, 150, 243, 0.06))',
                        border: '1px solid rgba(156, 39, 176, 0.25)',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                            What should change?
                        </Typography>
                        <IconButton size="small" onClick={() => { setShowRegen(false); setFeedback(''); setRegenError(null); }} aria-label="Cancel regeneration">
                            <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        autoFocus
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value.slice(0, 2000))}
                        placeholder={
                            "e.g. Shorten the bio. Mention the AWS migration. " +
                            "Fix the role title to 'Senior Engineer'. Drop the marketing language."
                        }
                        helperText={`${feedback.length}/2000 — leave empty for a free rewrite`}
                        variant="outlined"
                        size="small"
                        sx={{ bgcolor: 'white' }}
                    />
                    {regenError && <Alert severity="error" sx={{ mt: 1 }}>{regenError}</Alert>}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                        <Button
                            size="small"
                            onClick={() => { setShowRegen(false); setFeedback(''); setRegenError(null); }}
                            sx={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AutoAwesomeIcon />}
                            onClick={() => regenMutation.mutate()}
                            disabled={regenMutation.isPending}
                            sx={{
                                textTransform: 'none',
                                background: 'linear-gradient(135deg, #9c27b0, #2196f3)',
                                boxShadow: 'none',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7b1fa2, #1976d2)',
                                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.35)',
                                },
                            }}
                        >
                            {regenMutation.isPending ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                    </Box>
                </Box>
            </Collapse>

            <Fade in={isRegenerating} timeout={200} unmountOnExit>
                <Box
                    sx={{
                        mt: 1.5, px: 2, py: 1.5, borderRadius: 1,
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        bgcolor: 'rgba(156, 39, 176, 0.06)',
                        border: '1px dashed rgba(156, 39, 176, 0.35)',
                    }}
                >
                    <CircularProgress size={18} sx={{ color: '#9c27b0' }} />
                    <Typography variant="body2" sx={{ color: '#6a1b9a' }}>
                        AI is rewriting this entry with your feedback...
                    </Typography>
                </Box>
            </Fade>
        </Paper>
    );
};

// --- MAIN PAGE COMPONENT ---
const CompanyOnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const auth = useAuth();

    const [activeStep, setActiveStep] = useState(0);
    const [onboardingId, setOnboardingId] = useState<string | null>(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);

    const steps = [
        'Upload Documents',
        'Classify & Extract',
        'Review Workforce',
        'Review Projects',
        'Review Company Profile',
        'Complete'
    ];

    // --- CREATE ONBOARDING & UPLOAD FILES ---
    const handleStartOnboarding = async () => {
        if (uploadFiles.length === 0) return;
        setIsUploading(true);
        setUploadError(null);

        try {
            // Step 1: Create onboarding session and get presigned upload URLs
            const filesData = uploadFiles.map(f => ({
                filename: f.file.name,
                classification: f.classification,
            }));

            const result = await localApiService.createOnboarding(auth, filesData);
            const { onboardingId: newId, uploadUrls } = result;

            // Step 2: Upload files to S3 using presigned URLs
            const uploadPromises = uploadFiles.map((uf) => {
                const urlInfo = uploadUrls?.find((u: any) => u.filename === uf.file.name);
                if (!urlInfo?.upload_url) {
                    throw new Error(`No upload URL received for file: ${uf.file.name}`);
                }
                return localApiService.uploadFile(urlInfo.upload_url, uf.file);
            });

            await Promise.all(uploadPromises);

            // Step 3: Set ID to start polling
            setOnboardingId(newId);
            setActiveStep(1);
        } catch (err) {
            setUploadError(err instanceof Error ? err.message : 'Failed to start onboarding');
        } finally {
            setIsUploading(false);
        }
    };

    // --- POLL WORKBENCH ---
    const workbenchQuery = useQuery({
        queryKey: ['onboardingWorkbench', onboardingId],
        queryFn: () => localApiService.getWorkbench(onboardingId!, auth),
        enabled: !!onboardingId,
        refetchInterval: (query) => {
            const phase = query.state.data?.phase;
            if (isTransitioning) return 1000;
            if (['PHASE_1', 'PHASE_2', 'PHASE_3', 'DONE', 'FAILED'].includes(phase || '')) return false;
            return 2000;
        },
    });

    // --- APPROVE / SKIP PHASE ---
    const [mutationError, setMutationError] = useState<string | null>(null);

    const approveMutation = useMutation({
        mutationFn: () => localApiService.approveStage(onboardingId!, auth),
        onMutate: () => { setIsTransitioning(true); setMutationError(null); },
        onSuccess: () => workbenchQuery.refetch(),
        onError: (err: Error) => { setIsTransitioning(false); setMutationError(err.message || 'Failed to approve stage.'); }
    });

    const skipMutation = useMutation({
        mutationFn: () => localApiService.skipStage(onboardingId!, auth),
        onMutate: () => { setIsTransitioning(true); setMutationError(null); },
        onSuccess: () => workbenchQuery.refetch(),
        onError: (err: Error) => { setIsTransitioning(false); setMutationError(err.message || 'Failed to skip stage.'); }
    });

    // --- STATE SYNC ---
    const phase = workbenchQuery.data?.phase;
    const items = workbenchQuery.data?.items || [];

    useEffect(() => {
        if (!phase) return;
        if (phase === 'PHASE_1') { setActiveStep(2); }
        else if (phase === 'PHASE_2') { setActiveStep(3); setIsTransitioning(false); }
        else if (phase === 'PHASE_3') { setActiveStep(4); setIsTransitioning(false); }
        else if (phase === 'DONE') { setActiveStep(5); setIsTransitioning(false); }
        else if (phase === 'FAILED') { setIsTransitioning(false); }
        else if (phase === 'STARTING') { setActiveStep(1); }
    }, [phase]);

    // --- SORTING ---
    const statusPriority = { 'ADDITION': 0, 'MODIFICATION': 1, 'DUPLICATE': 2, 'NO_CHANGE': 3 };
    const sortedItems = [...items].sort((a, b) => {
        const pA = statusPriority[a.status_flag as keyof typeof statusPriority] ?? 4;
        const pB = statusPriority[b.status_flag as keyof typeof statusPriority] ?? 4;
        return pA - pB;
    });

    // Count items by status for summary
    const additionCount = items.filter(i => i.status_flag === 'ADDITION').length;
    const modificationCount = items.filter(i => i.status_flag === 'MODIFICATION').length;
    const noChangeCount = items.filter(i => i.status_flag === 'NO_CHANGE').length;

    // --- RENDER HELPERS ---
    const renderLoading = (msg: string) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
            <Typography variant="h6" color="text.secondary">{msg}</Typography>
            <Typography variant="body2" color="text.disabled">AI Agents are analyzing your documents...</Typography>
        </Box>
    );

    const renderFailure = (errorMsg: string) => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Processing Failed</Typography>
            <Typography variant="body1" color="error" sx={{ mb: 4 }}>{errorMsg}</Typography>
            <Button variant="contained" onClick={() => navigate('/tenderq')} sx={{ mt: 3 }}>
                Back to Tenders
            </Button>
        </Box>
    );

    const renderPhaseEditor = (
        title: string,
        description: string,
        approveText: string,
        showSkip: boolean = false
    ) => (
        <Box>
            <Paper sx={{ p: 3, mb: 3, bgcolor: '#e3f2fd', borderLeft: '6px solid #2196f3' }}>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>{description}</Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Chip label={`${additionCount} Additions`} color="success" size="small" />
                    <Chip label={`${modificationCount} Updates`} color="warning" size="small" />
                    <Chip label={`${noChangeCount} Unchanged`} size="small" />
                </Stack>
            </Paper>

            {sortedItems.map((item) => (
                <DiffItemCard key={item.SortKey} item={item} onboardingId={onboardingId!} auth={auth} />
            ))}

            {items.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">No items found for this phase. You can skip to the next phase.</Typography>
                </Box>
            )}

            <Box sx={{ mt: 4, pb: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
                {showSkip && (
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => skipMutation.mutate()}
                        disabled={approveMutation.isPending || skipMutation.isPending}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Skip Phase
                    </Button>
                )}
                <Button
                    variant="contained"
                    size="large"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => approveMutation.mutate()}
                    disabled={approveMutation.isPending || skipMutation.isPending}
                    sx={{ px: 6, py: 1.5, borderRadius: 2 }}
                >
                    {approveMutation.isPending ? "Processing..." : approveText}
                </Button>
            </Box>
        </Box>
    );

    const renderSuccess = () => (
        <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Onboarding Complete!</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                All approved changes have been applied to your company profile, workforce, and projects.
            </Typography>
            <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/tenderq')}
            >
                Back to Tenders
            </Button>
        </Box>
    );

    // --- MAIN RENDER ---
    return (
        <Box sx={{
            bgcolor: '#f8f9fa',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Fixed Header */}
            <Box sx={{
                p: 2,
                bgcolor: 'white',
                borderBottom: '1px solid #ddd',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
            }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tenderq')}>
                    Back to Tenders
                </Button>
            </Box>

            {/* Scrollable Content Area */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
                <Container maxWidth="md">
                    <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
                        <Typography variant="h5" gutterBottom fontWeight="bold">
                            Company Onboarding Agent
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                            Upload your company documents and the AI will extract and organize workforce members, past projects, and company profile information.
                        </Typography>

                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Paper>

                    {/* CONTENT AREA */}
                    <Paper elevation={1} sx={{ p: 4, minHeight: '400px', mb: 8 }}>

                        {/* STEP 0: FILE UPLOAD */}
                        {!onboardingId && !isUploading && (
                            <Box>
                                <Paper sx={{ p: 3, mb: 3, bgcolor: '#e8f5e9', borderLeft: '6px solid #4caf50' }}>
                                    <Typography variant="h6" gutterBottom>Upload Your Documents</Typography>
                                    <Typography variant="body2">
                                        Add all your files &mdash; CVs, project descriptions, company brochures, capability statements, certifications.
                                        The AI will automatically classify each file and extract structured data. You can also manually set the document type.
                                    </Typography>
                                </Paper>

                                <FileUploadArea
                                    files={uploadFiles}
                                    onFilesChange={setUploadFiles}
                                    disabled={false}
                                />

                                {uploadError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>
                                )}

                                <Box sx={{ mt: 4, textAlign: 'center' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<PlayArrowIcon />}
                                        onClick={handleStartOnboarding}
                                        disabled={uploadFiles.length === 0}
                                        sx={{ px: 6, py: 1.5 }}
                                    >
                                        Start Onboarding ({uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''})
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* UPLOADING STATE */}
                        {isUploading && (
                            <Box sx={{ textAlign: 'center', py: 8 }}>
                                <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
                                <Typography variant="h6" color="text.secondary">Uploading documents...</Typography>
                                <Typography variant="body2" color="text.disabled">Preparing files for AI processing</Typography>
                            </Box>
                        )}

                        {/* MUTATION ERROR */}
                        {mutationError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError(null)}>{mutationError}</Alert>
                        )}

                        {/* ERROR STATE */}
                        {workbenchQuery.isError && (
                            renderFailure(workbenchQuery.error.message)
                        )}

                        {/* FAILED PHASE (backend reported failure) */}
                        {phase === 'FAILED' && !workbenchQuery.isError && (
                            renderFailure(workbenchQuery.data?.message || 'The onboarding workflow encountered an error.')
                        )}

                        {/* LOADING / PROCESSING STATES */}
                        {onboardingId && !workbenchQuery.isError && phase !== 'FAILED' && (isTransitioning || approveMutation.isPending || skipMutation.isPending || phase === 'STARTING' || phase === 'PROCESSING' || phase === 'CLASSIFYING' || !phase) && !isUploading && (
                            renderLoading(
                                phase === 'CLASSIFYING' ? "Classifying documents..." :
                                activeStep <= 1 ? "Extracting data from documents..." :
                                activeStep === 2 ? "Processing workforce data..." :
                                activeStep === 3 ? "Processing project data..." :
                                "Enriching company profile..."
                            )
                        )}

                        {/* PHASE 1: REVIEW WORKFORCE */}
                        {phase === 'PHASE_1' && !isTransitioning && !workbenchQuery.isError && (
                            renderPhaseEditor(
                                "Review Workforce Import",
                                "The AI has extracted workforce member profiles from your CVs. Review each entry below. Items marked as 'Update' were matched against existing employees by name. You can change the action for each item.",
                                "Approve Workforce & Continue",
                                true
                            )
                        )}

                        {/* PHASE 2: REVIEW PROJECTS */}
                        {phase === 'PHASE_2' && !isTransitioning && !workbenchQuery.isError && (
                            renderPhaseEditor(
                                "Review Project Import",
                                "The AI has extracted past project information from your documents. Review and approve which projects should be added or updated in your company profile.",
                                "Approve Projects & Continue",
                                true
                            )
                        )}

                        {/* PHASE 3: REVIEW COMPANY PROFILE */}
                        {phase === 'PHASE_3' && !isTransitioning && !workbenchQuery.isError && (
                            renderPhaseEditor(
                                "Review Company Profile Enrichment",
                                "The AI has analyzed your company documents and generated or updated profile sections (e.g., sustainability strategy, quality policy). Review and approve the changes.",
                                "Approve & Finalize",
                                true
                            )
                        )}

                        {/* DONE */}
                        {phase === 'DONE' && !isTransitioning && !workbenchQuery.isError && (
                            renderSuccess()
                        )}

                    </Paper>
                </Container>
            </Box>
        </Box>
    );
};

export default CompanyOnboardingPage;
